import { OptionChainData, OptionsPrediction } from '../reducers/optionsSlice';

export const NEAR_THE_MONEY_PERCENT = 12;

export interface StrikeHighlight {
    call?: 'success' | 'missed';
    put?: 'success' | 'missed';
    callPredicted?: boolean;
    putPredicted?: boolean;
}

export const buildPastPeriodStrikeHighlights = (
    chain: OptionChainData,
    endPrice: number
): Record<number, StrikeHighlight> => {
    const highlights: Record<number, StrikeHighlight> = {};

    chain.calls.forEach((call) => {
        if (call.volume <= 0) {
            return;
        }
        if (endPrice > call.strike) {
            highlights[call.strike] = {
                ...highlights[call.strike],
                call: 'success',
            };
        }
    });

    chain.puts.forEach((put) => {
        if (put.volume <= 0) {
            return;
        }
        if (endPrice < put.strike) {
            highlights[put.strike] = {
                ...highlights[put.strike],
                put: 'success',
            };
        }
    });

    return highlights;
};

export const isNearTheMoney = (
    strike: number,
    spot: number,
    maxPercent: number = NEAR_THE_MONEY_PERCENT
): boolean => {
    if (spot <= 0) {
        return true;
    }

    return (Math.abs(strike - spot) / spot) * 100 <= maxPercent;
};

export const aggregatePastPeriodChains = (
    analyses: Array<{ chainData: OptionChainData }>
): OptionChainData => {
    const calls = new Map<number, number>();
    const puts = new Map<number, number>();

    analyses.forEach(({ chainData }) => {
        chainData.calls.forEach(({ strike, volume }) => {
            if (volume > 0) {
                calls.set(strike, (calls.get(strike) || 0) + volume);
            }
        });
        chainData.puts.forEach(({ strike, volume }) => {
            if (volume > 0) {
                puts.set(strike, (puts.get(strike) || 0) + volume);
            }
        });
    });

    return {
        symbol: analyses[0]?.chainData.symbol || '',
        expiration_date: 'past 3 months aggregate',
        calls: Array.from(calls.entries())
            .map(([strike, volume]) => ({ strike, volume }))
            .sort((a, b) => a.strike - b.strike),
        puts: Array.from(puts.entries())
            .map(([strike, volume]) => ({ strike, volume }))
            .sort((a, b) => a.strike - b.strike),
    };
};

const resolveForwardStrike = (pastStrike: number, forwardStrikes: number[]): number | null => {
    if (!forwardStrikes.length) {
        return null;
    }

    if (forwardStrikes.includes(pastStrike)) {
        return pastStrike;
    }

    return forwardStrikes.reduce((nearest, strike) =>
        (Math.abs(strike - pastStrike) < Math.abs(nearest - pastStrike) ? strike : nearest)
    );
};

export const buildNextPeriodStrikeHighlightsFromPastVolume = (
    forwardChain: OptionChainData,
    pastChain: OptionChainData,
    prediction: OptionsPrediction | null
): Record<number, StrikeHighlight> => {
    if (!prediction) {
        return {};
    }

    const forwardCallStrikes = forwardChain.calls
        .filter((call) => call.volume > 0)
        .map((call) => call.strike);
    const forwardPutStrikes = forwardChain.puts
        .filter((put) => put.volume > 0)
        .map((put) => put.strike);

    const topPastCalls = [...pastChain.calls]
        .filter((call) => call.volume > 0)
        .sort((a, b) => b.volume - a.volume);
    const topPastPuts = [...pastChain.puts]
        .filter((put) => put.volume > 0)
        .sort((a, b) => b.volume - a.volume);

    const highlights: Record<number, StrikeHighlight> = {};

    const markPastCallStrikes = (entries: typeof topPastCalls) => {
        entries.forEach((call) => {
            const forwardStrike = resolveForwardStrike(call.strike, forwardCallStrikes);
            if (forwardStrike !== null) {
                highlights[forwardStrike] = { ...highlights[forwardStrike], callPredicted: true };
            }
        });
    };

    const markPastPutStrikes = (entries: typeof topPastPuts) => {
        entries.forEach((put) => {
            const forwardStrike = resolveForwardStrike(put.strike, forwardPutStrikes);
            if (forwardStrike !== null) {
                highlights[forwardStrike] = { ...highlights[forwardStrike], putPredicted: true };
            }
        });
    };

    if (prediction.prediction === 'bullish') {
        markPastCallStrikes(topPastCalls.slice(0, 5));
    } else if (prediction.prediction === 'bearish') {
        markPastPutStrikes(topPastPuts.slice(0, 5));
    } else {
        markPastCallStrikes(topPastCalls.slice(0, 3));
        markPastPutStrikes(topPastPuts.slice(0, 3));
    }

    return highlights;
};

export const buildNextPeriodStrikeHighlights = (
    chain: OptionChainData,
    prediction: OptionsPrediction | null,
    currentPrice: number
): Record<number, StrikeHighlight> => {
    if (!prediction) {
        return {};
    }

    const highlights: Record<number, StrikeHighlight> = {};
    const nearCalls = [...chain.calls]
        .filter((call) => call.volume > 0 && isNearTheMoney(call.strike, currentPrice))
        .sort((a, b) => b.volume - a.volume);
    const nearPuts = [...chain.puts]
        .filter((put) => put.volume > 0 && isNearTheMoney(put.strike, currentPrice))
        .sort((a, b) => b.volume - a.volume);
    const topCalls = nearCalls.slice(0, 5);
    const topPuts = nearPuts.slice(0, 5);

    if (prediction.prediction === 'bullish') {
        topCalls.forEach((call) => {
            highlights[call.strike] = { ...highlights[call.strike], callPredicted: true };
        });
    } else if (prediction.prediction === 'bearish') {
        topPuts.forEach((put) => {
            highlights[put.strike] = { ...highlights[put.strike], putPredicted: true };
        });
    } else {
        topCalls.slice(0, 3).forEach((call) => {
            highlights[call.strike] = { ...highlights[call.strike], callPredicted: true };
        });
        topPuts.slice(0, 3).forEach((put) => {
            highlights[put.strike] = { ...highlights[put.strike], putPredicted: true };
        });
    }

    return highlights;
};

export interface StrikeWinVerification {
    valid: boolean;
    errors: string[];
    endPrice: number;
    callWins: number;
    putWins: number;
    callStrikesInChain: number;
    putStrikesInChain: number;
}

export const verifyPastStrikeWins = (
    chain: OptionChainData,
    endPrice: number,
    highlights: Record<number, StrikeHighlight> = buildPastPeriodStrikeHighlights(chain, endPrice)
): StrikeWinVerification => {
    const errors: string[] = [];
    const expected = buildPastPeriodStrikeHighlights(chain, endPrice);

    chain.calls.forEach((call) => {
        if (call.volume <= 0) {
            return;
        }

        const shouldWin = endPrice > call.strike;
        const markedWin = highlights[call.strike]?.call === 'success';

        if (shouldWin && !markedWin) {
            errors.push(`Call $${call.strike}: ITM at expiry ($${endPrice.toFixed(2)} > $${call.strike}) but not marked Win`);
        }
        if (!shouldWin && markedWin) {
            errors.push(`Call $${call.strike}: marked Win but OTM at expiry ($${endPrice.toFixed(2)} <= $${call.strike})`);
        }
    });

    chain.puts.forEach((put) => {
        if (put.volume <= 0) {
            return;
        }

        const shouldWin = endPrice < put.strike;
        const markedWin = highlights[put.strike]?.put === 'success';

        if (shouldWin && !markedWin) {
            errors.push(`Put $${put.strike}: ITM at expiry ($${endPrice.toFixed(2)} < $${put.strike}) but not marked Win`);
        }
        if (!shouldWin && markedWin) {
            errors.push(`Put $${put.strike}: marked Win but OTM at expiry ($${endPrice.toFixed(2)} >= $${put.strike})`);
        }
    });

    Object.entries(highlights).forEach(([strikeKey, highlight]) => {
        const strike = Number(strikeKey);
        const callInChain = chain.calls.some((call) => call.strike === strike && call.volume > 0);
        const putInChain = chain.puts.some((put) => put.strike === strike && put.volume > 0);

        if (highlight.call === 'success' && !callInChain) {
            errors.push(`Call $${strike}: Win highlight exists but strike is not in chain data`);
        }
        if (highlight.put === 'success' && !putInChain) {
            errors.push(`Put $${strike}: Win highlight exists but strike is not in chain data`);
        }
    });

    if (JSON.stringify(expected) !== JSON.stringify(highlights)) {
        errors.push('Stored highlights do not match recomputed ITM highlights from chain + expiry price');
    }

    return {
        valid: errors.length === 0,
        errors,
        endPrice,
        callWins: chain.calls.filter((call) => call.volume > 0 && endPrice > call.strike).length,
        putWins: chain.puts.filter((put) => put.volume > 0 && endPrice < put.strike).length,
        callStrikesInChain: chain.calls.filter((call) => call.volume > 0).length,
        putStrikesInChain: chain.puts.filter((put) => put.volume > 0).length,
    };
};

export interface NextPredictionVerification {
    valid: boolean;
    errors: string[];
    predictedCallStrikes: number[];
    predictedPutStrikes: number[];
    note: string;
}

export const verifyNextPeriodPredictionsFromPastVolume = (
    forwardChain: OptionChainData,
    pastChain: OptionChainData,
    prediction: OptionsPrediction,
    highlights: Record<number, StrikeHighlight> = buildNextPeriodStrikeHighlightsFromPastVolume(forwardChain, pastChain, prediction)
): NextPredictionVerification => {
    const errors: string[] = [];
    const expected = buildNextPeriodStrikeHighlightsFromPastVolume(forwardChain, pastChain, prediction);
    const forwardCallStrikes = forwardChain.calls.filter((call) => call.volume > 0).map((call) => call.strike);
    const forwardPutStrikes = forwardChain.puts.filter((put) => put.volume > 0).map((put) => put.strike);

    Object.entries(expected).forEach(([strikeKey, highlight]) => {
        const strike = Number(strikeKey);

        if (highlight.callPredicted && !forwardCallStrikes.includes(strike)) {
            errors.push(`Predicted call $${strike} is not present in forward chain volume data`);
        }

        if (highlight.putPredicted && !forwardPutStrikes.includes(strike)) {
            errors.push(`Predicted put $${strike} is not present in forward chain volume data`);
        }
    });

    Object.entries(highlights).forEach(([strikeKey, highlight]) => {
        const strike = Number(strikeKey);

        if (highlight.call === 'success' || highlight.put === 'success') {
            errors.push(`Forward chain strike $${strike} has Win highlight — wins only apply to past expired contracts`);
        }

        if (highlight.callPredicted && !expected[strike]?.callPredicted) {
            errors.push(`Call $${strike}: unexpected Pred highlight`);
        }
        if (highlight.putPredicted && !expected[strike]?.putPredicted) {
            errors.push(`Put $${strike}: unexpected Pred highlight`);
        }
    });

    if (JSON.stringify(expected) !== JSON.stringify(highlights)) {
        errors.push('Stored highlights do not match recomputed past-volume predictions');
    }

    return {
        valid: errors.length === 0,
        errors,
        predictedCallStrikes: Object.entries(expected)
            .filter(([, highlight]) => highlight.callPredicted)
            .map(([strike]) => Number(strike)),
        predictedPutStrikes: Object.entries(expected)
            .filter(([, highlight]) => highlight.putPredicted)
            .map(([strike]) => Number(strike)),
        note: 'Next 3 months uses Pred badges on forward strikes aligned with past 3M high-volume strikes + sentiment.',
    };
};

export const verifyNextPeriodPredictions = (
    chain: OptionChainData,
    prediction: OptionsPrediction,
    currentPrice: number,
    highlights: Record<number, StrikeHighlight> = buildNextPeriodStrikeHighlights(chain, prediction, currentPrice)
): NextPredictionVerification => {
    const errors: string[] = [];
    const expected = buildNextPeriodStrikeHighlights(chain, prediction, currentPrice);
    const callVolumeByStrike = new Map(chain.calls.map((call) => [call.strike, call.volume || 0]));
    const putVolumeByStrike = new Map(chain.puts.map((put) => [put.strike, put.volume || 0]));

    Object.entries(expected).forEach(([strikeKey, highlight]) => {
        const strike = Number(strikeKey);

        if (highlight.callPredicted && (callVolumeByStrike.get(strike) || 0) <= 0) {
            errors.push(`Predicted call $${strike} is not present in chain volume data`);
        }

        if (highlight.callPredicted && !isNearTheMoney(strike, currentPrice)) {
            errors.push(`Predicted call $${strike} is not near the money at $${currentPrice.toFixed(2)}`);
        }

        if (highlight.putPredicted && (putVolumeByStrike.get(strike) || 0) <= 0) {
            errors.push(`Predicted put $${strike} is not present in chain volume data`);
        }

        if (highlight.putPredicted && !isNearTheMoney(strike, currentPrice)) {
            errors.push(`Predicted put $${strike} is not near the money at $${currentPrice.toFixed(2)}`);
        }
    });

    Object.entries(highlights).forEach(([strikeKey, highlight]) => {
        const strike = Number(strikeKey);

        if (highlight.call === 'success' || highlight.put === 'success') {
            errors.push(`Forward chain strike $${strike} has Win highlight — wins only apply to past expired contracts`);
        }

        if (highlight.callPredicted && !expected[strike]?.callPredicted) {
            errors.push(`Call $${strike}: unexpected Pred highlight`);
        }
        if (highlight.putPredicted && !expected[strike]?.putPredicted) {
            errors.push(`Put $${strike}: unexpected Pred highlight`);
        }
    });

    return {
        valid: errors.length === 0,
        errors,
        predictedCallStrikes: Object.entries(expected)
            .filter(([, highlight]) => highlight.callPredicted)
            .map(([strike]) => Number(strike)),
        predictedPutStrikes: Object.entries(expected)
            .filter(([, highlight]) => highlight.putPredicted)
            .map(([strike]) => Number(strike)),
        note: 'Next 3 months uses Pred badges on near-the-money high-volume strikes aligned with sentiment.',
    };
};

export const getStockPriceOnDate = (
    bars: Array<{ t: string; c: number }>,
    dateStr: string
): number => {
    const exactMatch = bars.find((bar) => bar.t.split('T')[0] === dateStr);
    if (exactMatch) {
        return exactMatch.c;
    }

    const targetTime = new Date(`${dateStr}T23:59:59Z`).getTime();
    let closestBarOnOrBefore = bars[0];

    for (const bar of bars) {
        const barTime = new Date(bar.t).getTime();
        if (barTime <= targetTime) {
            closestBarOnOrBefore = bar;
            continue;
        }
        break;
    }

    return closestBarOnOrBefore?.c || 0;
};
